import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { v4_mailbox_access, v4_mailbox_set_access } from '../../actions/v4/mailBox';

const selectedFromUsers = (users, ownerId) => users
    .filter((user) => Number(user.IS_GRANTED) && !Number(user.IS_ADMIN)
        && Number(user.USER_ID) !== Number(ownerId))
    .map((user) => Number(user.USER_ID));

const sameIds = (first, second) => first.length === second.length
    && first.every((id) => second.includes(id));

const normalizedName = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();

const MailboxAccessPanel = ({ mailbox, onDirtyChange, onSavingChange, onSaved }) => {
    const [users, setUsers] = useState([]);
    const [savedIds, setSavedIds] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const loadAccess = () => {
        setLoading(true);
        setError('');
        v4_mailbox_access(mailbox.ID).then((response) => {
            if (!response.status) throw new Error(response.error || 'Could not load access.');
            const allUsers = response.result || [];
            const grants = selectedFromUsers(allUsers, mailbox.USER_ID);
            setUsers(allUsers);
            setSavedIds(grants);
            setSelectedIds(grants);
        }).catch((loadError) => {
            setError(loadError?.response?.data?.error || loadError.message || 'Could not load access.');
        }).finally(() => setLoading(false));
    };

    useEffect(() => {
        loadAccess();
        // This panel is keyed by mailbox ID, so it remounts when the account changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dirty = !sameIds(selectedIds, savedIds);
    useEffect(() => {
        onDirtyChange(dirty);
    }, [dirty, onDirtyChange]);

    const chosenUsers = useMemo(() => users.filter((user) =>
        selectedIds.includes(Number(user.USER_ID))), [users, selectedIds]);
    const matchingUsers = useMemo(() => {
        const term = normalizedName(query);
        return users.filter((user) => normalizedName(user.FULL_NAME).includes(term));
    }, [users, query]);

    const toggleUser = (userId) => setSelectedIds((current) => current.includes(userId)
        ? current.filter((id) => id !== userId) : [...current, userId]);

    const save = async () => {
        setSaving(true);
        onSavingChange(true);
        try {
            const response = await v4_mailbox_set_access(mailbox.ID, selectedIds);
            if (!response.status) throw new Error(response.error || 'Could not save access.');
            const grants = selectedFromUsers(response.result || [], mailbox.USER_ID);
            setSavedIds(grants);
            setSelectedIds(grants);
            onSaved(grants.length);
            toast.success(`Access saved for ${mailbox.EMAIL}`);
        } catch (saveError) {
            toast.error(saveError?.response?.data?.error || saveError.message || 'Could not save access.');
        } finally {
            setSaving(false);
            onSavingChange(false);
        }
    };

    return <section className="mail-settings-access" aria-labelledby="mail-settings-access-heading">
        <div className="mail-settings-section-heading">
            <div>
                <h3 id="mail-settings-access-heading">People with access</h3>
                <p>Choose who can read and send mail from this account.</p>
            </div>
            {!loading && !error && <span className="mail-settings-count">{selectedIds.length} assigned</span>}
        </div>

        <div className="mail-settings-inherited">
            <span className="mail-settings-inherited-icon" aria-hidden="true">✓</span>
            <span><strong>Always available:</strong> the account owner and admins. Their access cannot be removed here.</span>
        </div>

        {loading ? <div className="mail-settings-state">Loading people...</div> : error ?
            <div className="mail-settings-state mail-settings-state-error">
                <span>{error}</span>
                <button type="button" onClick={loadAccess}>Try again</button>
            </div> : <>
                <div className="mail-settings-subheading">Assigned users</div>
                {chosenUsers.length ? <div className="mail-settings-chips">
                    {chosenUsers.map((user) => <button
                        key={user.USER_ID}
                        type="button"
                        className="mail-settings-chip"
                        onClick={() => toggleUser(Number(user.USER_ID))}
                        aria-label={`Remove ${user.FULL_NAME} from ${mailbox.EMAIL}`}
                        title="Remove access"
                    >
                        <span>{user.FULL_NAME}</span><span aria-hidden="true">×</span>
                    </button>)}
                </div> : <p className="mail-settings-empty-access">No additional users assigned to this mailbox.</p>}

                <label className="mail-settings-search-label" htmlFor={`mail-settings-user-search-${mailbox.ID}`}>
                    Add a user
                </label>
                <div className="mail-settings-search-box">
                    <i className="bx bx-search" aria-hidden="true" />
                    <input id={`mail-settings-user-search-${mailbox.ID}`} type="search"
                        placeholder="Search all active users" value={query}
                        onChange={(event) => setQuery(event.target.value)} />
                </div>
                <div className="mail-settings-user-list">
                    {matchingUsers.length ? matchingUsers.map((user) => {
                        const isOwner = Number(user.USER_ID) === Number(mailbox.USER_ID);
                        const isAdmin = Number(user.IS_ADMIN) === 1;
                        const isAssigned = selectedIds.includes(Number(user.USER_ID));
                        const inheritedLabel = isOwner ? 'Owner · Always has access'
                            : isAdmin ? 'Admin · Always has access' : '';
                        const content = <>
                            <span className="mail-settings-avatar">{String(user.FULL_NAME || '?').trim().charAt(0).toUpperCase()}</span>
                            <span className="mail-settings-user-name">{user.FULL_NAME}</span>
                            <span className="mail-settings-user-status">{inheritedLabel || (isAssigned ? 'Assigned' : '+ Add')}</span>
                        </>;
                        return inheritedLabel
                            ? <div className="mail-settings-user-row mail-settings-user-row-inherited" key={user.USER_ID}>
                                {content}
                            </div>
                            : <button type="button" className="mail-settings-user-row" key={user.USER_ID}
                                aria-pressed={isAssigned}
                                onClick={() => toggleUser(Number(user.USER_ID))}>
                                {content}
                            </button>;
                    }) : <p className="mail-settings-user-empty">
                        {query ? 'No matching active users.' : 'No active users found.'}
                    </p>}
                </div>

                <div className="mail-settings-savebar">
                    <span>{dirty ? 'You have unsaved access changes.' : 'Access is up to date.'}</span>
                    <div className="mail-settings-save-actions">
                        {dirty && <button type="button" className="mail-settings-text-button"
                            onClick={() => setSelectedIds(savedIds)}>Discard changes</button>}
                        <button type="button" className="mail-settings-primary-button" disabled={!dirty || saving}
                            onClick={save}>{saving ? 'Saving...' : 'Save access'}</button>
                    </div>
                </div>
            </>}
    </section>;
};

export default MailboxAccessPanel;
